import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useStore } from '../context/StoreContext';
import { CoveristicLogo } from './CoveristicLogo';
import { 
  Search, 
  Menu, 
  X, 
  MessageCircle, 
  Sparkles,
  Smartphone,
  Palette,
  Frame,
  Shirt,
  Coffee,
  HelpCircle,
  ChevronRight,
  Tag,
  Flame,
  Globe,
  BookOpen
} from 'lucide-react';
import { getLocalizedPath, getCategoryPermalink } from '../utils/localization';

export const Header: React.FC = () => {
  const { 
    currentView, 
    setCurrentView, 
    activeCategoryFilter, 
    setActiveCategoryFilter,
    setSelectedProduct,
    searchQuery, 
    setSearchQuery,
    categories,
    language,
    setLanguage,
    t,
    isRtl
  } = useStore();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Lock body scroll and handle Escape key when mobile menu is open
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (mobileMenuOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setMobileMenuOpen(false);
        }
      };
      window.addEventListener('keydown', handleKeyDown);

      return () => {
        document.body.style.overflow = originalOverflow;
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [mobileMenuOpen]);

  const handleNavClick = (view: string, categoryFilter: string = 'all') => {
    setSelectedProduct(null);
    setCurrentView(view);
    setActiveCategoryFilter(categoryFilter);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, view: string, categoryFilter: string = 'all') => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    handleNavClick(view, categoryFilter);
  };

  const homeHref = getLocalizedPath('/', language);

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-neutral-200 transition-all duration-200">
      <div className="max-w-7xl mx-auto px-3 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20 gap-2 xl:gap-3 flex-nowrap">
          
          {/* Mobile Menu Button */}
          <div className="flex items-center lg:hidden shrink-0">
            <button
              id="mobile-menu-toggle-btn"
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 -ms-1 text-neutral-800 hover:text-[#E21B24] transition-colors rounded-md active:bg-neutral-100 touch-manipulation cursor-pointer"
              aria-label="Toggle Navigation Menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Logo */}
          <div className="flex items-center shrink-0">
            <a
              id="header-brand-logo-btn"
              href={homeHref}
              onClick={(e) => handleLinkClick(e, 'home', 'all')}
              className="text-left group transition-transform active:scale-95"
              aria-label="Coveristic Custom Print on Demand Studio Home"
            >
              <CoveristicLogo size="md" withTagline={false} />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav aria-label="Main Store Navigation" className="hidden md:flex items-center gap-1 xl:gap-2 shrink flex-nowrap overflow-x-auto scrollbar-none py-1">
            <a
              id="nav-home-btn"
              href={homeHref}
              onClick={(e) => handleLinkClick(e, 'home', 'all')}
              style={{ fontWeight: 500, fontStyle: 'normal' }}
              className={`px-2 py-1 xl:px-3 xl:py-1.5 text-xs xl:text-sm font-medium tracking-normal normal-case transition-colors rounded-sm whitespace-nowrap shrink-0 ${
                currentView === 'home' && activeCategoryFilter === 'all'
                  ? 'text-[#E21B24] bg-red-50'
                  : 'text-neutral-900 hover:text-[#E21B24]'
              }`}
            >
              {t.home}
            </a>

            <a
              id="nav-phone-cases-btn"
              href={getCategoryPermalink('phone-cases', language)}
              onClick={(e) => handleLinkClick(e, 'shop', 'phone-cases')}
              style={{ fontWeight: 500, fontStyle: 'normal' }}
              className={`px-2 py-1 xl:px-3 xl:py-1.5 text-xs xl:text-sm font-medium tracking-normal normal-case transition-colors rounded-sm whitespace-nowrap shrink-0 ${
                currentView === 'shop' && activeCategoryFilter === 'phone-cases'
                  ? 'text-[#E21B24] bg-red-50'
                  : 'text-neutral-900 hover:text-[#E21B24]'
              }`}
            >
              {t.phoneCases}
            </a>

            <a
              id="nav-canvas-btn"
              href={getCategoryPermalink('canvas', language)}
              onClick={(e) => handleLinkClick(e, 'shop', 'canvas')}
              style={{ fontWeight: 500, fontStyle: 'normal' }}
              className={`px-2 py-1 xl:px-3 xl:py-1.5 text-xs xl:text-sm font-medium tracking-normal normal-case transition-colors rounded-sm whitespace-nowrap shrink-0 ${
                currentView === 'shop' && activeCategoryFilter === 'canvas'
                  ? 'text-[#E21B24] bg-red-50'
                  : 'text-neutral-900 hover:text-[#E21B24]'
              }`}
            >
              {t.canvasWallArt}
            </a>

            <a
              id="nav-photo-frames-btn"
              href={getCategoryPermalink('photo-frames', language)}
              onClick={(e) => handleLinkClick(e, 'shop', 'photo-frames')}
              style={{ fontWeight: 500, fontStyle: 'normal' }}
              className={`px-2 py-1 xl:px-3 xl:py-1.5 text-xs xl:text-sm font-medium tracking-normal normal-case transition-colors rounded-sm whitespace-nowrap shrink-0 ${
                currentView === 'shop' && activeCategoryFilter === 'photo-frames'
                  ? 'text-[#E21B24] bg-red-50'
                  : 'text-neutral-900 hover:text-[#E21B24]'
              }`}
            >
              {t.photoFrames}
            </a>

            <a
              id="nav-mugs-btn"
              href={getCategoryPermalink('mugs', language)}
              onClick={(e) => handleLinkClick(e, 'shop', 'mugs')}
              style={{ fontWeight: 500, fontStyle: 'normal' }}
              className={`px-2 py-1 xl:px-3 xl:py-1.5 text-xs xl:text-sm font-medium tracking-normal normal-case transition-colors rounded-sm whitespace-nowrap shrink-0 ${
                currentView === 'shop' && activeCategoryFilter === 'mugs'
                  ? 'text-[#E21B24] bg-red-50'
                  : 'text-neutral-900 hover:text-[#E21B24]'
              }`}
            >
              {t.drinkingMugs}
            </a>

            <a
              id="nav-tshirts-btn"
              href={getCategoryPermalink('tshirts', language)}
              onClick={(e) => handleLinkClick(e, 'shop', 'tshirts')}
              style={{ fontWeight: 500, fontStyle: 'normal' }}
              className={`px-2 py-1 xl:px-3 xl:py-1.5 text-xs xl:text-sm font-medium tracking-normal normal-case transition-colors rounded-sm whitespace-nowrap shrink-0 ${
                currentView === 'shop' && (activeCategoryFilter === 'tshirts' || activeCategoryFilter === 'hoodies')
                  ? 'text-[#E21B24] bg-red-50'
                  : 'text-neutral-900 hover:text-[#E21B24]'
              }`}
            >
              {t.apparel}
            </a>

            <a
              id="nav-stickers-btn"
              href={getCategoryPermalink('stickers', language)}
              onClick={(e) => handleLinkClick(e, 'shop', 'stickers')}
              style={{ fontWeight: 500, fontStyle: 'normal' }}
              className={`px-2 py-1 xl:px-3 xl:py-1.5 text-xs xl:text-sm font-medium tracking-normal normal-case transition-colors rounded-sm whitespace-nowrap shrink-0 ${
                currentView === 'shop' && activeCategoryFilter === 'stickers'
                  ? 'text-[#E21B24] bg-red-50'
                  : 'text-neutral-900 hover:text-[#E21B24]'
              }`}
            >
              {t.stickers}
            </a>

            <a
              id="nav-custom-print-btn"
              href={getLocalizedPath('/custom-print', language)}
              onClick={(e) => handleLinkClick(e, 'custom-print', 'all')}
              style={{ fontWeight: 500, fontStyle: 'normal' }}
              className={`px-2 py-1 xl:px-3 xl:py-1.5 text-xs xl:text-sm font-medium tracking-normal normal-case transition-colors rounded-sm whitespace-nowrap shrink-0 ${
                currentView === 'custom-print'
                  ? 'text-[#E21B24] bg-red-50'
                  : 'text-neutral-900 hover:text-[#E21B24]'
              }`}
            >
              {t.customPrint}
            </a>

            {/* High Visibility Blog Nav Link */}
            <a
              id="nav-blog-btn"
              href={getLocalizedPath('/blog', language)}
              onClick={(e) => handleLinkClick(e, 'blog', 'all')}
              style={{ fontWeight: 600, fontStyle: 'normal' }}
              className={`px-2.5 py-1 xl:px-3 xl:py-1.5 text-xs xl:text-sm font-semibold tracking-normal normal-case transition-all rounded-md whitespace-nowrap flex items-center gap-1.5 shrink-0 ${
                currentView === 'blog'
                  ? 'text-white bg-[#E21B24] shadow-xs'
                  : 'text-neutral-900 hover:text-[#E21B24] hover:bg-red-50/70'
              }`}
            >
              <BookOpen className={`w-3.5 h-3.5 ${currentView === 'blog' ? 'text-white' : 'text-[#E21B24]'}`} />
              <span>{t.blog}</span>
            </a>

            <a
              id="nav-offers-btn"
              href={getLocalizedPath('/offers', language)}
              onClick={(e) => handleLinkClick(e, 'offers', 'all')}
              style={{ fontWeight: 600, fontStyle: 'normal' }}
              className={`px-2 py-1 xl:px-3 xl:py-1.5 text-xs xl:text-sm font-semibold tracking-normal normal-case transition-all rounded-sm whitespace-nowrap flex items-center gap-1 shrink-0 ${
                currentView === 'offers'
                  ? 'text-white bg-[#E21B24] shadow-xs'
                  : 'text-[#E21B24] hover:bg-red-50'
              }`}
            >
              <Flame className={`w-3.5 h-3.5 ${currentView === 'offers' ? 'fill-white text-white' : 'fill-[#E21B24] text-[#E21B24]'}`} />
              <span>{t.offers}</span>
            </a>

            <a
              id="nav-pricing-btn"
              href={getLocalizedPath('/pricing', language)}
              onClick={(e) => handleLinkClick(e, 'pricing', 'all')}
              style={{ fontWeight: 500, fontStyle: 'normal' }}
              className={`px-2 py-1 xl:px-3 xl:py-1.5 text-xs xl:text-sm font-medium tracking-normal normal-case transition-colors rounded-sm whitespace-nowrap shrink-0 ${
                currentView === 'pricing'
                  ? 'text-[#E21B24] bg-red-50'
                  : 'text-neutral-900 hover:text-[#E21B24]'
              }`}
            >
              {language === 'ar' ? 'الأسعار' : 'Pricing'}
            </a>

            <a
              id="nav-about-btn"
              href={getLocalizedPath('/about', language)}
              onClick={(e) => handleLinkClick(e, 'about', 'all')}
              style={{ fontWeight: 500, fontStyle: 'normal' }}
              className={`px-2 py-1 xl:px-3 xl:py-1.5 text-xs xl:text-sm font-medium tracking-normal normal-case transition-colors rounded-sm whitespace-nowrap shrink-0 ${
                currentView === 'about'
                  ? 'text-[#E21B24] bg-red-50'
                  : 'text-neutral-900 hover:text-[#E21B24]'
              }`}
            >
              {t.aboutUs}
            </a>

            <a
              id="nav-contact-btn"
              href={getLocalizedPath('/contact', language)}
              onClick={(e) => handleLinkClick(e, 'contact', 'all')}
              style={{ fontWeight: 500, fontStyle: 'normal' }}
              className={`px-2 py-1 xl:px-3 xl:py-1.5 text-xs xl:text-sm font-medium tracking-normal normal-case transition-colors rounded-sm whitespace-nowrap shrink-0 ${
                currentView === 'contact'
                  ? 'text-[#E21B24] bg-red-50'
                  : 'text-neutral-900 hover:text-[#E21B24]'
              }`}
            >
              {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </a>
          </nav>

          {/* Right Action Controls */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 flex-nowrap">
            {/* Quick Blog Access Pill Link (Mobile/Tablet only so anchor text is never duplicated with Desktop Nav) */}
            <a
              id="header-blog-quick-btn"
              href={getLocalizedPath('/blog', language)}
              onClick={(e) => handleLinkClick(e, 'blog', 'all')}
              className={`md:hidden flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 text-xs font-bold rounded-full border transition-all cursor-pointer shrink-0 ${
                currentView === 'blog'
                  ? 'bg-[#E21B24] text-white border-[#E21B24] shadow-xs'
                  : 'bg-white text-neutral-800 hover:text-[#E21B24] hover:bg-red-50/70 border-neutral-200'
              }`}
              title={language === 'ar' ? 'المدونة ودليل المدن اللبنانية' : 'Blog & Lebanon Guides'}
            >
              <BookOpen className={`w-3.5 h-3.5 ${currentView === 'blog' ? 'text-white' : 'text-[#E21B24]'}`} />
              <span className="font-extrabold">{language === 'ar' ? 'دليل المدن (٣٠)' : 'Guides (30)'}</span>
            </a>

            {/* Language Switcher Link (Arabic /ar/ <-> English /) */}
            <a
              id="language-switcher-btn"
              href={language === 'ar' ? '/' : '/ar/'}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
                e.preventDefault();
                setLanguage(language === 'ar' ? 'en' : 'ar');
              }}
              className="flex items-center gap-1 px-2 py-1 text-xs font-bold text-neutral-800 hover:text-[#E21B24] hover:bg-neutral-100 rounded border border-neutral-200 transition-all cursor-pointer shrink-0"
              title={language === 'ar' ? 'Switch to English (/)' : 'التحويل للنسخة العربية (/ar/)'}
            >
              <Globe className="w-3.5 h-3.5 text-[#E21B24]" />
              <span>{language === 'ar' ? 'EN' : 'عربي'}</span>
            </a>

            {/* Search Input / Trigger */}
            <div className="relative shrink-0">
              <form 
                role="search" 
                aria-label="Desktop Catalog Search" 
                toolname="search_products"
                tooldescription="Search Coveristic Lebanon custom print-on-demand catalog by keyword or category and view USD prices"
                onSubmit={(e) => e.preventDefault()}
                className="hidden sm:flex items-center bg-neutral-100 rounded-full px-2.5 py-1.5 border border-neutral-200 focus-within:border-[#E21B24] focus-within:bg-white transition-all w-24 md:w-28 xl:w-36"
              >
                <Search className="w-3.5 h-3.5 text-neutral-400 mr-1.5 shrink-0" aria-hidden="true" />
                <input
                  id="header-search-input"
                  name="search"
                  type="search"
                  aria-label="Search catalog products and designs"
                  placeholder={t.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (currentView !== 'shop' && currentView !== 'home') {
                      setCurrentView('shop');
                    }
                  }}
                  className="bg-transparent text-xs w-full focus:outline-none text-neutral-800 placeholder-neutral-400 font-medium"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search input"
                    className="text-neutral-400 hover:text-neutral-600 text-xs font-bold"
                  >
                    ×
                  </button>
                )}
              </form>

              {/* Mobile Search Button */}
              <button
                type="button"
                onClick={() => setSearchOpen(!searchOpen)}
                className="sm:hidden p-1.5 text-neutral-700 hover:text-[#E21B24]"
                aria-label="Open search input"
                aria-expanded={searchOpen}
              >
                <Search className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            {/* Direct WhatsApp Ordering Hotline Button - Never overflows viewport */}
            <a
              id="header-whatsapp-pill"
              href={language === 'ar' ? 'https://wa.me/96181773349?text=%D9%85%D8%B1%D8%AD%D8%A8%D8%A7%20Coveristic%21%20%D8%A8%D8%AF%D9%8A%20%D8%A7%D8%B3%D8%AA%D9%81%D8%B3%D8%B1%20%D8%A3%D9%88%20%D8%A7%D8%B7%D9%84%D8%A8%20%D9%82%D8%B7%D8%B9%D8%A9%20%D9%85%D8%B7%D8%A8%D9%88%D8%B9%D8%A9' : "https://wa.me/96181773349?text=Hello%20Coveristic!%20I'd%20like%20to%20order%20or%20inquire%20about%20a%20product"}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Direct WhatsApp Ordering Hotline"
              className="flex items-center gap-1 sm:gap-1.5 bg-[#25D366]/10 border border-[#25D366]/40 text-[#128C7E] hover:bg-[#25D366]/20 px-2 sm:px-2.5 py-1 rounded-full text-[11px] xl:text-xs font-black tracking-tight transition-all active:scale-95 shadow-xs whitespace-nowrap shrink-0"
              title="Chat & Order on WhatsApp (+961 81 773 349)"
            >
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#25D366]"></span>
              </span>
              <MessageCircle className="w-3.5 h-3.5 text-[#25D366] fill-[#25D366] shrink-0" />
              <span className="hidden sm:inline font-extrabold whitespace-nowrap">{language === 'ar' ? 'واتساب' : 'WhatsApp'}</span>
            </a>
          </div>
        </div>

        {/* Mobile Search Dropdown */}
        {searchOpen && (
          <div className="sm:hidden pb-3 pt-1 border-t border-neutral-100">
            <form 
              role="search" 
              aria-label="Mobile Catalog Search" 
              onSubmit={(e) => e.preventDefault()}
              className="flex items-center bg-neutral-100 rounded-lg px-3 py-2 border border-neutral-200"
            >
              <Search className="w-4 h-4 text-neutral-400 mr-2" aria-hidden="true" />
              <input
                id="header-mobile-search-input"
                name="mobile-search"
                type="search"
                aria-label="Search mobile catalog products and designs"
                placeholder={t.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (currentView !== 'shop') setCurrentView('shop');
                }}
                className="bg-transparent text-xs w-full focus:outline-none"
                autoFocus
              />
              {searchQuery && (
                <button 
                  type="button" 
                  onClick={() => setSearchQuery('')} 
                  aria-label="Clear mobile search"
                  className="text-neutral-500 text-sm"
                >
                  ✕
                </button>
              )}
            </form>
          </div>
        )}
      </div>

      {/* Mobile Drawer Menu (Portaled directly to document.body with z-[999999] so it is NEVER trapped behind other elements) */}
      {mobileMenuOpen && typeof document !== 'undefined' && createPortal(
        <div 
          id="mobile-navigation-portal" 
          className="fixed inset-0 z-[999999] lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label={language === 'ar' ? 'القائمة الرئيسية' : 'Main navigation'}
        >
          {/* Backdrop Overlay */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity duration-200 z-0 cursor-pointer"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Drawer Navigation Sheet Container */}
          <div 
            className={`fixed inset-y-0 ${isRtl ? 'right-0 animate-drawer-right' : 'left-0 animate-drawer-left'} z-10 w-[88vw] max-w-sm h-full flex flex-col pointer-events-auto shadow-2xl`}
          >
            <nav 
              aria-label="Mobile Primary Navigation" 
              className="w-full h-full bg-white flex flex-col justify-between overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drawer Top Header Bar */}
              <div className="flex items-center justify-between px-4 py-3.5 border-b border-neutral-200 bg-neutral-50 shrink-0">
              <button
                type="button"
                onClick={() => handleNavClick('home', 'all')}
                className="text-left cursor-pointer"
              >
                <CoveristicLogo size="sm" withTagline={false} />
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-neutral-800 bg-white hover:bg-neutral-100 rounded border border-neutral-300 transition-colors cursor-pointer"
                >
                  <Globe className="w-3.5 h-3.5 text-[#E21B24]" />
                  <span>{language === 'ar' ? 'EN' : 'عربي'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-1.5 text-neutral-600 hover:text-[#E21B24] rounded-full hover:bg-neutral-200 transition-colors cursor-pointer"
                  aria-label="Close navigation menu"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Scrollable Links Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {/* Currency Badge */}
              <div className="flex items-center justify-between pb-2 border-b border-neutral-200">
                <span className="text-[11px] font-bold text-neutral-500 uppercase tracking-wider">
                  {language === 'ar' ? 'العملة المعتمدة' : 'Currency'}
                </span>
                <span className="text-[11px] font-bold text-neutral-700 bg-neutral-100 px-2 py-0.5 rounded border border-neutral-200">
                  {language === 'ar' ? 'الدفع بالدولار ($ USD)' : 'Prices in USD ($)'}
                </span>
              </div>

              {/* Prominent WhatsApp Hotline Bar inside Mobile Menu */}
              <a
                href={language === 'ar' ? 'https://wa.me/96181773349?text=%D9%85%D8%B1%D8%AD%D8%A8%D8%A7%D9%8B%20Coveristic%21%20%D8%A3%D9%88%D8%AF%20%D8%A7%D9%84%D8%A7%D8%B3%D8%AA%D9%81%D8%B3%D8%A7%D8%B1%20%D8%A3%D9%88%20%D8%B7%D9%84%D8%A8%20%D9%85%D9%86%D8%AA%D8%AC' : "https://wa.me/96181773349?text=Hello%20Coveristic!%20I%20would%20like%20to%20order%20a%20product"}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 px-3 bg-[#25D366] hover:bg-[#20ba59] text-white font-extrabold text-xs sm:text-sm uppercase rounded-lg shadow-sm active:scale-98 transition-all shrink-0 my-1 text-center cursor-pointer"
              >
                <MessageCircle className="w-4 h-4 fill-white text-transparent shrink-0" />
                <span className="truncate">{language === 'ar' ? 'تواصل معنا واتساب (+961 81 773 349)' : 'Order on WhatsApp (+961 81 773 349)'}</span>
              </a>

              <p className="text-[10px] font-black tracking-wider text-neutral-400 uppercase pt-1">
                {t.customProducts}
              </p>

              <a
                id="mobile-nav-offers-btn"
                href={getLocalizedPath('/offers', language)}
                onClick={(e) => handleLinkClick(e, 'offers', 'all')}
                style={{ fontWeight: 600, fontStyle: 'normal' }}
                className="w-full flex items-center justify-between py-2.5 px-3 text-sm font-semibold normal-case rounded-lg bg-red-50 text-[#E21B24] border border-red-200 text-left mb-1.5 shadow-xs cursor-pointer hover:bg-red-100 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Flame className="w-4 h-4 fill-[#E21B24] text-[#E21B24]" />
                  <span>{t.offers}</span>
                </span>
                <span className="text-[10px] bg-[#E21B24] text-white font-bold px-2 py-0.5 rounded">{language === 'ar' ? 'عروضات' : 'Deals'}</span>
              </a>

              {/* Featured Blog & Lebanon Guides Card in Mobile Menu Drawer */}
              <a
                id="mobile-drawer-featured-blog-btn"
                href={getLocalizedPath('/blog', language)}
                onClick={(e) => handleLinkClick(e, 'blog')}
                style={{ fontStyle: 'normal' }}
                className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all text-left rtl:text-right cursor-pointer mb-2 ${
                  currentView === 'blog'
                    ? 'bg-red-50 border-[#E21B24] text-[#E21B24] ring-1 ring-[#E21B24]'
                    : 'bg-gradient-to-r from-red-50/70 via-white to-red-50/40 hover:bg-red-50 border-red-200 text-neutral-900 shadow-xs'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    currentView === 'blog' ? 'bg-[#E21B24] text-white shadow-xs' : 'bg-red-100 text-[#E21B24]'
                  }`}>
                    <BookOpen className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-extrabold text-sm text-neutral-900">{t.blog}</span>
                    </div>
                    <p className="text-[11px] text-neutral-500 font-normal mt-0.5">
                      {language === 'ar' ? 'دليل ومقالات الطباعة في كافة مدن لبنان' : 'Print on demand guides across Lebanon'}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#E21B24] rtl:rotate-180 shrink-0" />
              </a>
              
              <a
                href={homeHref}
                onClick={(e) => handleLinkClick(e, 'home', 'all')}
                style={{ fontWeight: 500, fontStyle: 'normal' }}
                className="w-full flex items-center justify-between py-2.5 px-3 text-sm font-medium normal-case rounded-lg hover:bg-neutral-100 text-neutral-900 text-left cursor-pointer transition-colors"
              >
                <span>{t.home}</span>
                <ChevronRight className="w-4 h-4 text-neutral-400" />
              </a>

              <a
                href={getCategoryPermalink('phone-cases', language)}
                onClick={(e) => handleLinkClick(e, 'shop', 'phone-cases')}
                style={{ fontWeight: 500, fontStyle: 'normal' }}
                className="w-full flex items-center justify-between py-2.5 px-3 text-sm font-medium normal-case rounded-lg hover:bg-neutral-100 text-neutral-900 text-left cursor-pointer transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-[#E21B24]" />
                  {t.phoneCases}
                </span>
                <span className="text-[10px] bg-red-100 text-[#E21B24] font-medium px-2 py-0.5 rounded">{language === 'ar' ? 'الأكتر طلباً' : 'Popular'}</span>
              </a>

              <a
                href={getCategoryPermalink('canvas', language)}
                onClick={(e) => handleLinkClick(e, 'shop', 'canvas')}
                style={{ fontWeight: 500, fontStyle: 'normal' }}
                className="w-full flex items-center justify-between py-2.5 px-3 text-sm font-medium normal-case rounded-lg hover:bg-neutral-100 text-neutral-900 text-left cursor-pointer transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Palette className="w-4 h-4 text-[#E21B24]" />
                  {t.canvasWallArt}
                </span>
                <ChevronRight className="w-4 h-4 text-neutral-400" />
              </a>

              <a
                href={getCategoryPermalink('photo-frames', language)}
                onClick={(e) => handleLinkClick(e, 'shop', 'photo-frames')}
                style={{ fontWeight: 500, fontStyle: 'normal' }}
                className="w-full flex items-center justify-between py-2.5 px-3 text-sm font-medium normal-case rounded-lg hover:bg-neutral-100 text-neutral-900 text-left cursor-pointer transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Frame className="w-4 h-4 text-[#E21B24]" />
                  {t.photoFrames}
                </span>
                <span className="text-[10px] bg-red-100 text-[#E21B24] font-medium px-2 py-0.5 rounded">{language === 'ar' ? 'جديد' : 'New'}</span>
              </a>

              <a
                href={getCategoryPermalink('mugs', language)}
                onClick={(e) => handleLinkClick(e, 'shop', 'mugs')}
                style={{ fontWeight: 500, fontStyle: 'normal' }}
                className="w-full flex items-center justify-between py-2.5 px-3 text-sm font-medium normal-case rounded-lg hover:bg-neutral-100 text-neutral-900 text-left cursor-pointer transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Coffee className="w-4 h-4 text-[#E21B24]" />
                  {t.drinkingMugs}
                </span>
                <ChevronRight className="w-4 h-4 text-neutral-400" />
              </a>

              <a
                href={getCategoryPermalink('tshirts', language)}
                onClick={(e) => handleLinkClick(e, 'shop', 'tshirts')}
                style={{ fontWeight: 500, fontStyle: 'normal' }}
                className="w-full flex items-center justify-between py-2.5 px-3 text-sm font-medium normal-case rounded-lg hover:bg-neutral-100 text-neutral-900 text-left cursor-pointer transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Shirt className="w-4 h-4 text-[#E21B24]" />
                  {t.apparel}
                </span>
                <ChevronRight className="w-4 h-4 text-neutral-400" />
              </a>

              <a
                href={getCategoryPermalink('stickers', language)}
                onClick={(e) => handleLinkClick(e, 'shop', 'stickers')}
                style={{ fontWeight: 500, fontStyle: 'normal' }}
                className="w-full flex items-center justify-between py-2.5 px-3 text-sm font-medium normal-case rounded-lg hover:bg-neutral-100 text-neutral-900 text-left cursor-pointer transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#E21B24]" />
                  {t.stickers}
                </span>
                <ChevronRight className="w-4 h-4 text-neutral-400" />
              </a>

              <div className="border-t border-neutral-200 my-2 pt-2">
                <p className="text-[10px] font-medium tracking-wider text-neutral-400 normal-case mb-1">
                  {language === 'ar' ? 'خدمات إضافية' : 'Customer Services'}
                </p>

                <a
                  href={getLocalizedPath('/custom-print', language)}
                  onClick={(e) => handleLinkClick(e, 'custom-print')}
                  style={{ fontWeight: 500, fontStyle: 'normal' }}
                  className="w-full flex items-center justify-between py-2.5 px-3 text-sm font-medium normal-case rounded-lg hover:bg-neutral-100 text-neutral-900 text-left cursor-pointer transition-colors"
                >
                  <span>{t.customPrint}</span>
                  <ChevronRight className="w-4 h-4 text-neutral-400" />
                </a>

                <a
                  href={getLocalizedPath('/about', language)}
                  onClick={(e) => handleLinkClick(e, 'about')}
                  style={{ fontWeight: 500, fontStyle: 'normal' }}
                  className="w-full flex items-center justify-between py-2.5 px-3 text-sm font-medium normal-case rounded-lg hover:bg-neutral-100 text-neutral-900 text-left mt-1 cursor-pointer transition-colors"
                >
                  <span>{t.aboutUs} &amp; {t.faq}</span>
                  <ChevronRight className="w-4 h-4 text-neutral-400" />
                </a>

                <a
                  href={getLocalizedPath('/contact', language)}
                  onClick={(e) => handleLinkClick(e, 'contact')}
                  style={{ fontWeight: 500, fontStyle: 'normal' }}
                  className="w-full flex items-center justify-between py-2.5 px-3 text-sm font-medium normal-case rounded-lg hover:bg-neutral-100 text-neutral-900 text-left mt-1 cursor-pointer transition-colors"
                >
                  <span>{language === 'ar' ? 'تواصل مع الاستوديو' : 'Reach Our Studio'}</span>
                  <ChevronRight className="w-4 h-4 text-neutral-400" />
                </a>
              </div>

              {/* Direct WhatsApp Action in Drawer */}
              <a
                href={language === 'ar' ? 'https://wa.me/96181773349?text=%D9%85%D8%B1%D8%AD%D8%A8%D8%A7%D9%8B%20Coveristic%21%20%D8%A3%D9%88%D8%AF%20%D8%A7%D9%84%D8%A7%D8%B3%D8%AA%D9%81%D8%B3%D8%A7%D8%B1%20%D8%A3%D9%88%20%D8%B7%D9%84%D8%A8%20%D9%85%D9%86%D8%AA%D8%AC' : "https://wa.me/96181773349?text=Hello%20Coveristic!%20I%20would%20like%20to%20order%20a%20product"}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 px-3 bg-[#25D366] hover:bg-[#20ba59] text-white font-extrabold text-xs sm:text-sm uppercase rounded-lg shadow-md active:scale-95 transition-all text-center cursor-pointer"
              >
                <MessageCircle className="w-4 h-4 fill-white text-transparent shrink-0" />
                <span className="truncate">{language === 'ar' ? 'اطلب وتواصل عبر واتساب (+961 81 773 349)' : 'Chat & Order on WhatsApp (+961 81 773 349)'}</span>
              </a>
            </div>
          </nav>
        </div>
      </div>,
      document.body
    )}
    </header>
  );
};
